import React, { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import PageHeader from "pages/components/PageHeader";
import ProjectTable from "pages/components/ProjectTable/ProjectTable";

import DialogObjetivoV2Detalhe from "pages/comercial/objetivosV2/DialogObjetivoV2Detalhe";
import DialogObjetivoV2Editar from "pages/comercial/objetivosV2/DialogObjetivoV2Editar";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

function toISODate(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function fmtNumero(value, casas = 3) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const n = Number(value);

  if (!Number.isFinite(n)) {
    return "";
  }

  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

function casasPorMetrica(metrica, tipo = "resultado", modo = "") {
  const m = String(metrica || "")
    .trim()
    .toUpperCase();

  const modoNorm = String(modo || "")
    .trim()
    .toUpperCase();

  if (m === "CLIENTES") {
    return modoNorm === "MEDIA" ? 3 : 0;
  }

  if (m === "QTD") {
    return 0;
  }

  if (m === "EUROS") {
    return 2;
  }

  if (m === "LITROS") {
    return tipo === "objetivo" ? 0 : 3;
  }

  return 2;
}

function fmtPercentagem(value, casas = 2) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const n = Number(value);

  if (!Number.isFinite(n)) {
    return "";
  }

  return `${n.toLocaleString("pt-PT", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  })}%`;
}

export default function ObjetivosV2() {
  const { showAlert } = useAlert();
  const { temFuncao, userData } = useAuth();

  const isAdminLike =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openDetalhe, setOpenDetalhe] = useState(false);
  const [linhaDetalhe, setLinhaDetalhe] = useState(null);

  const [openEditar, setOpenEditar] = useState(false);
  const [linhaEditar, setLinhaEditar] = useState(null);

  const [openApagar, setOpenApagar] = useState(false);
  const [linhaApagar, setLinhaApagar] = useState(null);

  const carregar = async () => {
    setLoading(true);

    try {
      const [payload, err] = await handleApi(api.post("Comercial/SelObjetivosV2Resumo"));

      if (err) {
        showAlert(err, "error");
        return;
      }

      const rows = Array.isArray(payload) ? payload : payload?.data || [];

      setData(rows);
    } finally {
      setLoading(false);
    }
  };

  const abrirEditar = (row) => {
    const id = Number(row?.id || 0);

    if (id <= 0) {
      showAlert("Objetivo inválido.", "error");
      return;
    }

    setLinhaEditar(row);
    setOpenEditar(true);
  };

  const abrirDetalhe = (row) => {
    const id = Number(row?.id || 0);

    if (id <= 0) {
      showAlert("Objetivo inválido.", "error");
      return;
    }

    setLinhaDetalhe(row);
    setOpenDetalhe(true);
  };

  const abrirApagar = (row) => {
    const id = Number(row?.id || 0);

    if (id <= 0) {
      showAlert("Objetivo inválido.", "error");
      return;
    }

    setLinhaApagar(row);
    setOpenApagar(true);
  };

  const copiarObjetivo = async (row) => {
    const id = Number(row?.id || 0);

    if (id <= 0) {
      showAlert("Objetivo inválido.", "error");
      return;
    }

    setLoading(true);

    try {
      const [, err, meta] = await handleApi(
        api.post("Comercial/CopiarObjetivoV2", {
          id,
          criadopor:
            userData?.nome || userData?.username || userData?.email || String(userData?.id || ""),
        })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      showAlert(meta?.message ?? "Objetivo V2 copiado.", meta?.severity ?? "success");

      await carregar();
    } finally {
      setLoading(false);
    }
  };

  const apagarObjetivo = async () => {
    const id = Number(linhaApagar?.id || 0);

    if (id <= 0) {
      showAlert("Objetivo inválido.", "error");
      return;
    }

    setLoading(true);

    try {
      const [, err, meta] = await handleApi(
        api.post("Comercial/DelObjetivoV2", {
          id,
        })
      );

      if (err) {
        showAlert(err, "error");
        return;
      }

      showAlert(meta?.message ?? "Objetivo V2 apagado.", meta?.severity ?? "success");

      setOpenApagar(false);
      setLinhaApagar(null);

      await carregar();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: "Objetivo",
        accessor: "objetivo_nome",
        width: 220,
      },
      {
        Header: "Grupo",
        accessor: "grupo_nome",
        width: 100,
      },
      {
        Header: "Início",
        accessor: "dataini",
        width: 110,
        Cell: ({ value }) => toISODate(value),
      },
      {
        Header: "Fim",
        accessor: "datafim",
        width: 110,
        Cell: ({ value }) => toISODate(value),
      },
      {
        Header: "Métrica",
        accessor: "metrica",
        width: 100,
      },
      {
        Header: "Patamares",
        accessor: "total_patamares",
        width: 100,
        align: "center",
        headerAlign: "center",
      },
      {
        Header: "Objetivo",
        accessor: "valor_objetivo",
        width: 110,
        align: "right",
        headerAlign: "right",
        Cell: ({ value, row }) =>
          fmtNumero(
            value,
            casasPorMetrica(row?.original?.metrica, "objetivo", row?.original?.patamares_modo)
          ),
      },
      {
        Header: "Resultado",
        accessor: "resultado",
        width: 110,
        align: "right",
        headerAlign: "right",
        Cell: ({ value, row }) =>
          fmtNumero(
            value,
            casasPorMetrica(row?.original?.metrica, "resultado", row?.original?.patamares_modo)
          ),
      },
      {
        Header: "Atingimento",
        accessor: "atingimento_percentagem",
        width: 120,
        align: "right",
        headerAlign: "right",
        Cell: ({ value }) => fmtPercentagem(value, 2),
      },
      {
        Header: "Contrapartida",
        accessor: "contrapartida_percentagem",
        width: 130,
        align: "right",
        headerAlign: "right",
        Cell: ({ value }) =>
          value === null || value === undefined ? "" : fmtPercentagem(value, 2),
      },
      {
        Header: "Estado",
        accessor: "inativo",
        width: 100,
        Cell: ({ value }) => (Number(value) === 1 ? "Inativo" : "Ativo"),
      },
    ],
    []
  );

  const rowActions = useMemo(
    () => [
      {
        id: "detalhe",
        tooltip: "Ver detalhe",
        icon: <InfoOutlinedIcon fontSize="small" />,
        color: "info",
        onClick: abrirDetalhe,
      },
      {
        id: "editar",
        tooltip: "Editar objetivo",
        icon: <EditIcon fontSize="small" />,
        color: "error",
        onClick: abrirEditar,
      },
      ...(isAdminLike
        ? [
            {
              id: "copiar",
              tooltip: "Copiar objetivo",
              icon: <ContentCopyIcon fontSize="small" />,
              color: "success",
              onClick: copiarObjetivo,
            },
            {
              id: "apagar",
              tooltip: "Apagar objetivo",
              icon: <DeleteIcon fontSize="small" />,
              color: "error",
              onClick: abrirApagar,
            },
          ]
        : []),
    ],
    [isAdminLike, userData]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <PageHeader />

      <MDBox mt={2}>
        <Card>
          <MDBox p={2}>
            <MDBox mb={2} display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <MDButton
                color="info"
                onClick={() => {
                  setLinhaEditar(null);
                  setOpenEditar(true);
                }}
                sx={{
                  width: {
                    xs: "100%",
                    sm: "auto",
                  },
                }}
              >
                Novo
              </MDButton>
            </MDBox>

            {loading ? (
              <MDBox py={3} display="flex" justifyContent="center">
                <CircularProgress size={28} />
              </MDBox>
            ) : (
              <ProjectTable
                tableKey="objetivos-v2-resumo"
                version="v1"
                columns={columns}
                data={data}
                rowActions={rowActions}
                actionsPosition="left"
                emptyMessage="Sem objetivos V2 para apresentar."
                minWidth={1150}
                maxHeight={620}
                showKeepFilters
              />
            )}

            {!loading && data.length === 0 && (
              <MDTypography
                variant="button"
                color="text"
                sx={{
                  display: "block",
                  textAlign: "center",
                  mt: 1,
                }}
              >
                Não foram encontrados objetivos V2.
              </MDTypography>
            )}
          </MDBox>
        </Card>
      </MDBox>

      <DialogObjetivoV2Detalhe
        open={openDetalhe}
        onClose={() => {
          setOpenDetalhe(false);
          setLinhaDetalhe(null);
        }}
        objetivo={linhaDetalhe}
      />

      <DialogObjetivoV2Editar
        open={openEditar}
        onClose={() => {
          setOpenEditar(false);
          setLinhaEditar(null);
        }}
        onSaved={carregar}
        objetivo={linhaEditar}
      />

      <Dialog
        open={openApagar}
        onClose={() => {
          if (loading) return;

          setOpenApagar(false);
          setLinhaApagar(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Apagar objetivo</DialogTitle>

        <DialogContent>
          <MDTypography variant="body2">
            Tens a certeza de que pretendes apagar o objetivo{" "}
            <strong>{linhaApagar?.objetivo_nome || ""}</strong>?
          </MDTypography>

          <MDTypography variant="caption" color="error" display="block" mt={1}>
            Esta operação elimina também toda a configuração e resultados associados ao objetivo.
          </MDTypography>
        </DialogContent>

        <DialogActions>
          <MDButton
            color="secondary"
            onClick={() => {
              setOpenApagar(false);
              setLinhaApagar(null);
            }}
            disabled={loading}
          >
            Cancelar
          </MDButton>

          <MDButton color="error" onClick={apagarObjetivo} disabled={loading}>
            {loading ? <CircularProgress size={20} color="inherit" /> : "Apagar"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
